import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import { Separator } from 'app/components/ui/separator';
import { Button } from 'app/components/ui/button';
import { Textarea } from 'app/components/ui/textarea';
import {
  Shield,
  Eye,
  EyeOff,
  AlertTriangle,
  Key,
  Zap,
  RefreshCw,
  AlertCircle,
  Save,
  CheckCircle,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface MasterPasswordSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public: 0 for all keys
const createMasterPasswordSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all master password settings (sensitive data)
  const settingsKeys = [
    'master_password_enabled',
    'master_password_hash',
    'master_password_salt',
    'master_password_algorithm',
    'master_password_min_length',
    'master_password_require_special_chars',
    'master_password_require_numbers',
    'master_password_require_uppercase',
    'master_password_expiry_days',
    'master_password_max_attempts',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function MasterPasswordSettings({
  form,
}: MasterPasswordSettingsProps) {
  const [showMasterPassword, setShowMasterPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGeneratingPassword, setIsGeneratingPassword] = useState(false);
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createMasterPasswordSettingsPayload(formData);

      console.log(
        'Master password settings payload with is_public: 0:',
        payload,
      );

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('Master password settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating master password settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  const generateStrongPassword = () => {
    setIsGeneratingPassword(true);
    // Generate a strong 32-character password
    const chars =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    form.setValue('master_password', result);
    form.setValue('master_password_confirm', result);
    setIsGeneratingPassword(false);
  };

  return (
    <>
      {/* Master Password Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Master Password Configuration
          </CardTitle>
          <CardDescription>
            Configure master password functionality for administrative access to
            any user account.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              id="master_password_enabled"
              checked={form.watch('master_password_enabled')}
              onCheckedChange={value =>
                form.setValue('master_password_enabled', value)
              }
            />
            <Label
              htmlFor="master_password_enabled"
              className="text-base font-medium"
            >
              Enable Master Password
            </Label>
          </div>
          <p className="text-sm text-muted-foreground">
            When enabled, administrators can access any user account using the
            master password.
          </p>

          {form.watch('master_password_enabled') && (
            <>
              <Separator />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="master_password">Master Password</Label>
                  <div className="relative">
                    <Input
                      id="master_password"
                      type={showMasterPassword ? 'text' : 'password'}
                      {...form.register('master_password')}
                      placeholder="Enter master password"
                      className="pr-20"
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex gap-1">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={generateStrongPassword}
                        disabled={isGeneratingPassword}
                      >
                        {isGeneratingPassword ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Zap className="w-3 h-3" />
                        )}
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setShowMasterPassword(!showMasterPassword)
                        }
                      >
                        {showMasterPassword ? (
                          <EyeOff className="w-3 h-3" />
                        ) : (
                          <Eye className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    This password will be used to access any user account
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="master_password_confirm">
                    Confirm Master Password
                  </Label>
                  <div className="relative">
                    <Input
                      id="master_password_confirm"
                      type={showConfirmPassword ? 'text' : 'password'}
                      {...form.register('master_password_confirm')}
                      placeholder="Confirm master password"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1 h-6 w-6 p-0"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="w-3 h-3" />
                      ) : (
                        <Eye className="w-3 h-3" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Re-enter the master password to confirm
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Usage Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="w-5 h-5" />
            Usage Instructions
          </CardTitle>
          <CardDescription>
            How to use the master password functionality.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-medium text-blue-900">
                  How to Use Master Password
                </h4>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Go to the login page</li>
                  <li>Enter any valid username or email address</li>
                  <li>
                    Enter the master password instead of the user's password
                  </li>
                  <li>Complete 2FA if required</li>
                  <li>You will be logged in as that user</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-medium text-yellow-900">
                  Security Warning
                </h4>
                <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
                  <li>
                    Master password access is logged for security auditing
                  </li>
                  <li>
                    Use this feature only for legitimate administrative purposes
                  </li>
                  <li>
                    Regular users cannot use master password to access other
                    accounts
                  </li>
                  <li>
                    Keep the master password secure and share only with
                    authorized personnel
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Master password settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save master password settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
